import {vinRe} from '@deliveriesondemand/dod-regexes';
import {formatMoney, formatNumber} from 'accounting';
import {TriBool, VehicleColors} from '@deliveriesondemand/dod-data';
import {abbreviations} from '@deliveriesondemand/dod-states';
import {Component, Fragment} from 'react';
import {connect} from 'react-redux';
import handlers from 'react-handlers';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';

import Accordion from '-/components/general/Accordion';
import {Control, ControlGroup} from '-/components/fields/Control';
import {MoneyInput, NumberInput, PercentageInput} from '-/components/fields/NumberInput';
import {Radio, RadioGroup} from '-/components/fields/RadioGroup';
import AccessoryMultiSelect from '-/components/fields/AccessoryMultiSelect';
import ApiService from '-/services/ApiService';
import Checkbox from '-/components/fields/Checkbox';
import Form from '-/components/fields/Form';
import Legend from '-/components/fields/Legend';
import NavigationService from '-/services/NavigationService';
import Select from '-/components/fields/Select';
import BuyerInfoService from '-/services/BuyerInfoService';
import SessionService from '-/services/SessionService';
import TextInput from '-/components/fields/TextInput';
import UIService from '-/services/UIService';
import {isMobile} from '-/util/util';
import MonthYearInput from '-/components/fields/MonthYearInput';
import {capitalize, get, merge} from '-/util/lodash';
import config from '-/config/config';
import Vin from '-/components/fields/vehicle/Vin';
import Trim from '-/components/fields/vehicle/Trim';
import License from '-/components/fields/vehicle/License';
import YearMakeModel from '-/components/fields/vehicle/YearMakeModel';
import VehicleInfoService from '-/services/VehicleInfoService';
import DealInfoService from '-/services/DealInfoService';

const {disableLicenseLookup: disableLicense} = config;

const errorMsg = {
    color: 'red',
    border: '2px solid red',
    padding: 10,
    backgroundColor: 'white'
};

const styles = theme => ({
    accessoryField: {
        marginTop: 20,
        paddingTop: 15
    },
    badge: {
        width: 20,
        height: 20,
        border: '1px solid gray',
        position: 'absolute',
        margin: 10,
        left: '10px',
        zIndex: 2
    },
    deal: {
        width: '100%',
        marginBottom: 15,
        border: `2px solid ${theme.colors.primary}`,
        backgroundColor: 'white',
        padding: '5px',
        fontSize: 20,
        '& th, td': {
            textAlign: 'center',
            width: '33%'
        }
    },
    invalidVehicle: {
        color: 'red',
        border: '2px solid red',
        padding: 10,
        backgroundColor: 'white',
        margin: '20px 0',
        ...errorMsg
    },
    faqLink: {
        color: theme.colors.primary,
        cursor: 'pointer',
        fontWeight: 'bold'
    },
    loanInfo: {
        marginBottom: 15,
        '& h2': {
            textAlign: 'left'
        },
        '& p': {
            marginBottom: 10
        }
    },
    paddingTop: {
        paddingTop: 20
    }
});

class IdentifyVehicle extends Component {
    static propTypes = {
        handleVehicleChange: PropTypes.func.isRequired,
        showErrors: PropTypes.bool
    };

    constructor(props) {
        super(props);

        this.state = {
            errorMessages: [],
            hasSalvageLoan: 'UNKNOWN',
            invalidVehicleYear: false,
            showDealInfo: true,
            showFAQ: false,
            vinFeedbackMsg: null,
            vinTrims: []
        };

        this.onInputChange = this.onInputChange.bind(this);
        this.onExtraInfoTypeChange = this.onExtraInfoTypeChange.bind(this);
        this.onAddReminder = this.onAddReminder.bind(this);
    }

    componentDidMount() {
        const {
            currMonthlyPayment: priorCurrMonthlyPayment,
            existingRate: priorExistingRate,
            handleVehicleChange,
            isCU,
            mileageExact = false,
            payoff: priorPayoff,
            vehicleInfoType,
            vin
        } = this.props;

        const showDealInfo = priorCurrMonthlyPayment && priorExistingRate && priorPayoff;
        const errorMessages = this.getErrorMessages();

        handleVehicleChange({
            vehicleProps: this.props,
            vehicleState: {
                ...this.state,
                errorMessages
            }
        });

        this.setState({
            priorCurrMonthlyPayment,
            priorExistingRate,
            priorPayoff,
            showDealInfo
        });

        if (vin) {
            // If we already have a VIN, push user to VIN workflow
            VehicleInfoService.set('vehicleInfoType', 'vin');
        } else {
            VehicleInfoService.set('vehicleInfoType', vehicleInfoType);
        }

        if (isCU) {
            // Preset some default values
            VehicleInfoService.set('mileageExact', mileageExact);
        }
    }

    getErrorMessages() {
        const {
            color,
            handleInvalidateSubmission,
            isCU,
            knowRegistrationState,
            make,
            mileage,
            model,
            registrationState,
            style,
            vehicleInfoType,
            vin,
            year,
            yearRange
        } = this.props;
        const {maxYear, minYear} = yearRange;
        const needsColor = registrationState !== 'TX';
        let messages = [];
        let isInvalid = false;

        if (year && (year < minYear || year > maxYear)) {
            messages.push(`Vehicle year should be between ${minYear} and ${maxYear}`);
            isInvalid = true;
        }

        this.setState({invalidVehicleYear: isInvalid});
        handleInvalidateSubmission({isInvalid});

        if (isCU) {
            if (needsColor && color === '') {
                messages.push('Please select the vehicle color');
            }
    
            if (!mileage) {
                messages.push('You must include mileage');
            }
    
            if (!style) {
                messages.push('You must select a Trim');
            }
    
            if (knowRegistrationState && !registrationState) {
                messages.push('Registration State must be selected');
            }
        }
        
        const validVin = vin && vin.match(vinRe);

        if (['license', 'vin'].includes(vehicleInfoType) && !validVin) {
            messages.push('Invalid VIN');
        } else if (vehicleInfoType === 'neither' && (!year || !make || !model)) {
            messages.push('Year, make, and model must be selected');
        }

        return messages;
    }

    onInputChange() {
        setTimeout(() => {
            const {handleVehicleChange} = this.props;
            const errorMessages = this.getErrorMessages();

            this.setState({errorMessages}, () => {
                handleVehicleChange({
                    vehicleProps: this.props,
                    vehicleState: this.state
                });
            });
        }, 0);
    }

    async onAddReminder(extraStipTransportType, stipType) {
        const stipTypes = stipType.split(',');

        VehicleInfoService.set('extraStipTransportType', extraStipTransportType);

        let {hasVinReminder, hasRegReminder} = this.props;
        
        if (stipTypes.includes('VIN')) {
            hasVinReminder = true;
        }

        if (stipTypes.includes('REG')) {
            hasRegReminder = true;
        }

        NavigationService.set('hasVinReminder', hasVinReminder);
        NavigationService.set('hasRegReminder', hasRegReminder);

        VehicleInfoService.closeDialog();
        await this.saveVehicleInfo();

        VehicleInfoService.set('extraStipTransportType', null);
        NavigationService.set('setVinReminder', hasVinReminder);
        NavigationService.set('setRegReminder', hasRegReminder);
    }

    onTypeChange = type => {
        this.onExtraInfoTypeChange({
            target: {
                checked: true,
                value: type
            }
        });
    };

    onExtraInfoTypeChange(e) {
        const {onVehicleTypeChange} = this.props;
        const {value, checked} = e.target;

        if (checked) {
            VehicleInfoService.set('vehicleInfoType', value);
            // clear out all fields
            const fields = ['year', 'model', 'make', 'style', 'vin', 'styleDesc', 'styleOptions', 'ucgVehicleId'];

            fields.forEach(field => {
                VehicleInfoService.set(field, undefined);
            });

            VehicleInfoService.setAccessories([]);
            this.onInputChange();
            onVehicleTypeChange();
        }
    }

    async saveVehicleInfo() {
        const {collectNewDealInfo = false} = this.state;
        const parts = ['vehicleInfo', ...collectNewDealInfo ? ['dealInfo'] : []];
        const nextStep = DealInfoService.isCashDeal() ? 'finalReview' : 'employmentInfo';

        await ApiService.save(parts);

        UIService.stepCompleted('vehicleInfo');
        NavigationService.next(nextStep);
    }

    handleVinFeedback(feedback) {
        this.setState({vinFeedbackMsg: feedback});

        if (feedback) {
            VehicleInfoService.set('vinVehicleInfo', '');
        }
    }

    handleNewDealCheckboxChange = ({checked}) => {
        this.setState({
            collectNewDealInfo: checked
        });

        DealInfoService.set('notFromPrescreen', checked);
    };

    handleYearMonthChange = purchaseDate => {
        if (purchaseDate) {
            DealInfoService.set('newPurchaseDate', purchaseDate);
        }
    };

    handleFAQClick = () => {
        const {showFAQ} = this.state;

        this.setState({
            showFAQ: !showFAQ
        });
    };

    handleSalvageLoanChange = e => {
        const value = get(e, 'currentTarget.value');
        const {handleInvalidateSubmission} = this.props;

        this.setState({
            hasSalvageLoan: value
        });

        VehicleInfoService.set('salvageLoan', value);

        handleInvalidateSubmission({isInvalid: value === 'YES'});
    };

    onVehicleChange = vehicleObj => {
        for (const [key, value] of Object.entries(vehicleObj)) {
            VehicleInfoService.set(key, value);
        }
    };

    onAccessoryChange = (info = []) => {
        VehicleInfoService.setAccessories(info);
    };

    onClearVehicleFields = (fields = []) => {
        fields.forEach(field => {
            VehicleInfoService.set(field, undefined);
        });
    };

    onVinChange = data => {
        const {vehicleInfoType} = this.props;
        const {trims = [], vehicle = {}} = data;

        if (vehicleInfoType === 'vin') {
            VehicleInfoService.setExperianData(data);
        }

        this.onVehicleChange(vehicle);
        this.setState({vinTrims: trims});
    };

    render() {
        const {
            accessories,
            buyerName,
            color: vehicleColor,
            contactPhone,
            isCU,
            isDealer,
            licensePlate,
            licenseState,
            newPurchaseDate,
            newCurrMonthlyPayment,
            newExistingRate,
            newOriginalTerm,
            vin,
            year,
            make,
            model,
            stockNumber,
            style,
            mileage,
            onMileageChange,
            vehicleInfoType,
            onMileageExactChange,
            mileageExact,
            lienholderName,
            onLienholderNameChange,
            registrationState,
            knowRegistrationState,
            addressState,
            onRegistrationStateChange,
            onKnowRegistrationStateChange,
            onStockNumberChange,
            onVehicleColorChange,
            prioritizedMakes,
            classes = {},
            onCurrMonthlyPaymentChange,
            onExistingRateChange,
            onOriginalTermChange,
            isCCRMode,
            showErrors,
            ucgVehicleId,
            yearRange
        } = this.props;
        const {
            collectNewDealInfo,
            hasSalvageLoan,
            invalidVehicleYear,
            priorCurrMonthlyPayment,
            priorExistingRate,
            priorPayoff,
            showDealInfo,
            showFAQ,
            vinTrims
        } = this.state;

        const addressStateObj = {[addressState]: abbreviations[addressState]};
        const weighted = merge(addressStateObj, abbreviations);
        let vehicleTypeText;
        let vehicleTypeTitle;

        switch(vehicleInfoType) {
            case 'vin':
                vehicleTypeTitle = 'Your Vehicle Identification Number (VIN)';
                vehicleTypeText = `Please provide us with the Vehicle Identification Number
                (VIN) for the vehicle. If you don't have this
                information right now, you can try entering your License Plate number or we
                can collect it later. If you aren't sure where to find your VIN, check our
                Vehicle FAQ below.`;
                break;
            case 'license':
                vehicleTypeTitle = 'Your License Plate Number';
                vehicleTypeText = `Please provide us with your License Plate number and
                we will try to find some information about your vehicle. If you don't know
                your License Plate number, you can try one of our other vehicle lookup methods.`;
                break;
            case 'neither':
                vehicleTypeTitle = 'Your Vehicle\'s Year, Make, Model, and Trim Package';
                vehicleTypeText = `Please provide us with the year, make, model, and trim
                package for the vehicle. It's ok if you don't know
                every piece of information. Many users aren't sure about their vehicle's
                year or trim package. Get as close as you can and we'll help you find the
                rest of the information in a few steps`;
                break;
        }

        const vehicleInfo = {
            licensePlate,
            licenseState,
            year,
            make,
            model,
            style,
            ucgVehicleId,
            vin
        };

        const yearMakeModel = !!(year && make && model) && vehicleInfoType !== 'license';
        const isVinValid = vin && vinRe.test(vin) && vinTrims.length;
        const showTrim = isVinValid || yearMakeModel;
        const showAccessories = style && ucgVehicleId;
        const needsColor = registrationState !== 'TX';
        const disableLicenseLookup = isDealer ? true : disableLicense;

        return (
            <Form>
                <ControlGroup>
                    <Legend>Vehicle Information</Legend>
                    {showDealInfo && (
                        <div>
                            <div className="vehicleInfo">
                                <p className="info">
                                    Which vehicle does the following loan information apply to?
                                </p>
                                <table className={classes.deal}>
                                    <thead>
                                        <tr>
                                            <th scope="col">PAYMENT</th>
                                            <th scope="col">BALANCE</th>
                                            <th scope="col">RATE</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td data-label="Payment">{formatMoney(priorCurrMonthlyPayment)}</td>
                                            <td data-label="Balance">{formatMoney(priorPayoff)}</td>
                                            <td data-label="Rate">{formatNumber(priorExistingRate)}%</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                            {isCCRMode && (
                                <table className={classes.loanInfo}>
                                    <tbody>
                                        <tr>
                                            <td>
                                                <Checkbox handleCheckboxChange={this.handleNewDealCheckboxChange}>Different Car</Checkbox>
                                            </td>
                                        </tr>
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )}
                    {collectNewDealInfo && (
                        <ControlGroup className={classes.loanInfo} labelWidth={200}>
                            <h3>Loan Info</h3>
                            <p>
                                To the best of your abilities, please provide us with
                                the loan information for the vehicle you wish to refinance.
                            </p>
                            <Control>
                                <MonthYearInput
                                    label="Purchase Date"
                                    placeholder="MM/YYYY"
                                    value={newPurchaseDate}
                                    handleYearMonthChange={this.handleYearMonthChange} />
                            </Control>
                            <Control>
                                <NumberInput
                                    label="Original Term"
                                    placeholder="e.g. 27"
                                    value={newOriginalTerm}
                                    onChange={handlers(onOriginalTermChange, this.onInputChange)} />
                            </Control>
                            <Control>
                                <PercentageInput
                                    label="Rate"
                                    placeholder="e.g. 6.5%"
                                    value={newExistingRate}
                                    onChange={handlers(onExistingRateChange, this.onInputChange)} />
                            </Control>
                            <Control>
                                <MoneyInput
                                    label="Monthly Payment"
                                    placeholder="$XXX"
                                    value={newCurrMonthlyPayment}
                                    onChange={handlers(onCurrMonthlyPaymentChange, this.onInputChange)} />
                            </Control>
                        </ControlGroup>
                    )}
                    <Legend>{vehicleTypeTitle}</Legend>
                    <p className="info">
                        {vehicleTypeText}
                    </p>
                    <p className="info">
                        <a className={classes.faqLink} onClick={this.handleFAQClick}>
                            {!showFAQ ? 'Have Questions? Check our FAQ' : 'Hide FAQ'}
                        </a>
                    </p>
                    {(showFAQ && vehicleInfoType === 'vin') && (
                        <Accordion>
                            <div label="Why Do You Need My VIN?" isOpen>
                                <p>
                                    We use your VIN to lookup information about your
                                    vehicle so that we can accurately calculate its current valuation.
                                    This lookup will not affect you or your vehicle in any way.
                                </p>
                            </div>
                            <div label="Where Can I Find My VIN?">
                                <p>You can find your VIN in the following paperwork:</p>
                                <ul>
                                    <li>Your insurance card</li>
                                    <li>Your vehicle title</li>
                                    <li>Your vehicle registration</li>
                                </ul>
                                <p>
                                    If these documents are not readily available, you can
                                    also find your VIN on your vehicle in the following
                                    locations:
                                </p>
                                <ul>
                                    <li>Driver Side Door Frame</li>
                                    <li>Driver Side Dashboard</li>
                                </ul>
                            </div>
                        </Accordion>
                    )}
                    {(showFAQ && vehicleInfoType === 'license') && (
                        <Accordion>
                            <div label="Why Do You Need My License Number?" isOpen>
                                <p>
                                    We use your License Plate number to find your VIN. We
                                    can then use your VIN to lookup information about your
                                    vehicle so that we can accurately calculate its current valuation.
                                    This lookup will not affect you or your vehicle in any way.
                                </p>
                            </div>
                            <div label="Where Can I Find My License Number?">
                                <p>
                                    If you do not know your license plate number off the top of
                                    your head, you can usually find it in these locations:</p>
                                <ul>
                                    <li>Your insurance card</li>
                                    <li>Your vehicle registration</li>
                                </ul>
                            </div>
                        </Accordion>
                    )}
                    {(showFAQ && vehicleInfoType === 'neither') && (
                        <Accordion>
                            <div label="What If I Don&apos;t Know Some Of This Information?" isOpen>
                                <p>
                                    It&apos;s OK! Many users have trouble remembering
                                    their vehicle&apos;s exact year or trim package. Try
                                    and get as close as possible and we will help you
                                    find more information about your vehicle in a few
                                    more steps.
                                </p>
                            </div>
                            <div label="What If I Don&apos;t See My Vehicle?">
                                <p>
                                    Sometimes the vehicle can be too new or old for us to
                                    find the Make, Model, and Style information. You might
                                    have also experienced a glitch in the system. You can
                                    try again, or talk with one of our helpful Call
                                    Center Representatives.
                                </p>
                            </div>
                        </Accordion>
                    )}
                    <RadioGroup vertical={isMobile()}>
                        {!disableLicenseLookup && (
                            <Radio
                                name="vehicleInfoType"
                                value="license"
                                label="License Plate Number"
                                id="vehicleInfoTypePlate"
                                checked={vehicleInfoType === 'license'}
                                onChange={this.onExtraInfoTypeChange} />
                        )}
                        <Radio
                            name="vehicleInfoType"
                            value="vin"
                            label="I have my VIN"
                            id="vehicleInfoTypeVin"
                            checked={vehicleInfoType === 'vin'}
                            onChange={this.onExtraInfoTypeChange} />
                        <Radio
                            name="vehicleInfoType"
                            value="neither"
                            label="I don't have my VIN"
                            id="vehicleInfoTypeNeither"
                            checked={vehicleInfoType === 'neither'}
                            onChange={this.onExtraInfoTypeChange} />
                    </RadioGroup>
                </ControlGroup>
                {(!disableLicenseLookup && vehicleInfoType === 'license') && (
                    <License
                        addressState={addressState}
                        buyerName={buyerName}
                        contactPhone={contactPhone}
                        handleChange={handlers(this.onVehicleChange, this.onInputChange)}
                        handleTypeChange={this.onTypeChange}
                        vehicleInfo={vehicleInfo} />
                )}
                {((vehicleInfoType === 'license' && vin) || vehicleInfoType === 'vin') && (
                    <Vin
                        buyerName={buyerName}
                        handleChange={handlers(this.onVinChange, this.onInputChange)}
                        includeExperian={vehicleInfoType === 'vin'}
                        includeNADA={true}
                        vehicleInfo={vehicleInfo}
                        showErrors={showErrors} />
                )}
                {vehicleInfoType === 'neither' && (
                    <YearMakeModel
                        contactPhone={contactPhone}
                        handleChange={handlers(this.onVehicleChange, this.onInputChange)}
                        handleClearFields={handlers(this.onClearVehicleFields, this.onInputChange)}
                        prioritizedMakes={prioritizedMakes}
                        vehicleInfo={vehicleInfo}
                        yearRange={yearRange}
                        showErrors={showErrors} />
                )}
                {showTrim && (
                    <Trim
                        contactPhone={contactPhone}
                        handleChange={handlers(this.onVehicleChange, this.onInputChange)}
                        handleAccessoriesChange={handlers(this.onAccessoryChange, this.onInputChange)}
                        vehicleInfo={vehicleInfo}
                        trims={vinTrims}
                        showErrors={showErrors} />
                )}
                {showAccessories && (
                    <AccessoryMultiSelect
                        accessoriesSelected={accessories}
                        ucgVehicleId={ucgVehicleId}
                        handleCheckboxToggle={this.onAccessoryChange}
                        vin={vin} />
                )}
                {invalidVehicleYear && (
                    <p className={classes.invalidVehicle}>
                        The VIN you entered is associated with a vehicle that is outside of
                        this Creditor&apos;s year threshold. Please enter another VIN
                        or call {contactPhone} if you would like further assistance.
                    </p>
                )}
                {!invalidVehicleYear && (
                    <Fragment>
                        <ControlGroup labelWidth={200} className={classes.paddingTop}>
                            <Control>
                                <NumberInput
                                    label="Mileage"
                                    placeholder="e.g. 100,000"
                                    value={mileage}
                                    onChange={handlers(onMileageChange, this.onInputChange)} />
                            </Control>
                            {isCU && 
                                <RadioGroup
                                    labelWidth={200}
                                    vertical={isMobile()}
                                    label="Estimated/Exact"
                                    onChange={handlers(onMileageExactChange, this.onInputChange)}>
                                    <Radio
                                        name="mileageExact"
                                        value="estimated"
                                        label="Estimated"
                                        id="mileageExact-estimated"
                                        checked={!mileageExact} />
                                    <Radio
                                        name="mileageExact"
                                        value="exact"
                                        label="Exact"
                                        id="mileageExact-exact"
                                        checked={!!mileageExact} />
                                </RadioGroup>
                            }
                        </ControlGroup>
                        {isCU &&
                            <Fragment>
                                <ControlGroup labelWidth={200}>
                                    <Legend>Vehicle Registration</Legend>
                                    <RadioGroup
                                        labelWidth={200}
                                        vertical={isMobile()}
                                        label="Do you know the vehicle&apos;s registration state?">
                                        <Radio
                                            name="knowRegistrationState"
                                            value="yes"
                                            label="Yes"
                                            id="knowRegistrationState-yes"
                                            checked={knowRegistrationState === 'yes'}
                                            onChange={handlers(onKnowRegistrationStateChange.bind(null, addressState), this.onInputChange)} />
                                        <Radio
                                            name="knowRegistrationState"
                                            value="no"
                                            label="No"
                                            id="knowRegistrationState-no"
                                            checked={knowRegistrationState === 'no'}
                                            onChange={handlers(onKnowRegistrationStateChange.bind(null, addressState), this.onInputChange)} />
                                    </RadioGroup>
                                    {knowRegistrationState === 'yes' && (
                                        <Control>
                                            <Select
                                                label="Registration State"
                                                onChange={handlers(onRegistrationStateChange, this.onInputChange)}>
                                                <option>Select a State</option>
                                                {Object.keys(weighted).map((state, i) => {
                                                    return (
                                                        <option
                                                            key={i}
                                                            selected={state === registrationState}
                                                            value={state}>
                                                            {abbreviations[state]}
                                                        </option>
                                                    );
                                                })}
                                            </Select>
                                        </Control>
                                    )}
                                    {needsColor &&
                                        <Control>
                                            <Select
                                                className="vehicle-color"
                                                label="Vehicle Color"
                                                onChange={handlers(onVehicleColorChange, this.onInputChange)}>
                                                <option value="">Select a Color</option>
                                                {Object.keys(VehicleColors).filter(color => color !== 'OTHER').map((color, i) => {
                                                    const colorEntry = VehicleColors[color];
                                                    const {hex, label} = colorEntry;
                                                    const startAdornment = (
                                                        <div
                                                            className={classes.badge}
                                                            style={{backgroundColor: hex}} />
                                                    );

                                                    return (
                                                        <option
                                                            startAdornment={startAdornment}
                                                            key={`${label}-${i}`}
                                                            selected={color === vehicleColor}
                                                            value={color}>
                                                            {label}
                                                        </option>
                                                    );
                                                })}
                                            </Select>
                                        </Control>
                                    }
                                    <RadioGroup
                                        labelWidth={200}
                                        vertical={isMobile()}
                                        label="Does this vehicle have a salvage loan?">
                                        {TriBool.map(value => {
                                            return (
                                                <Radio
                                                    key={`${value}-box`}
                                                    name="salvageLoan"
                                                    value={value}
                                                    label={capitalize(value)}
                                                    checked={hasSalvageLoan === value}
                                                    onChange={this.handleSalvageLoanChange} />
                                            );
                                        })}
                                    </RadioGroup>
                                    {hasSalvageLoan === 'YES' && (
                                        <p className={classes.invalidVehicle}>
                                            We cannot currently refinance salvage loans.  If you have
                                            any questions, please call {contactPhone}.
                                        </p>
                                    )}
                                </ControlGroup>
                                <ControlGroup labelWidth={220}>
                                    <Legend>Lienholder Information</Legend>
                                    <Control>
                                        <TextInput
                                            label="Who is your car loan with?"
                                            value={lienholderName}
                                            onChange={onLienholderNameChange} />
                                    </Control>
                                </ControlGroup>
                            </Fragment>
                        }
                    </Fragment>
                )}
            </Form>
        );
    }
}

const mapStateToProps = state => {
    const buyerInfo = BuyerInfoService.getReducerState();
    const vehicleInfo = VehicleInfoService.getReducerState();
    const dealInfo = DealInfoService.getReducerState();
    const buyerName = get(buyerInfo, 'buyerInfo.name');
    const {
        currMonthlyPayment,
        existingRate,
        newCurrMonthlyPayment,
        newExistingRate,
        newPurchaseDate,
        newOriginalTerm,
        payoff
    } = dealInfo;

    const {
        bookout: {
            accessories = []
        } = {},
        knowRegistrationState = 'yes',
        licensePlate: {
            number: licensePlate,
            state: licenseState
        } = {},
        registrationState,
        style
    } = vehicleInfo;

    const {
        config: {
            contactPhone
        },
        creditor: {
            prioritizedMakes = [],
            yearRange = {}
        } = {}
    } = state;

    const addressState = get(state, 'buyerInfo.buyerInfo.address.state');
    const cellphone = get(state, 'buyerInfo.buyerInfo.cellphone');
    const dealType = get(state, 'creditor.type');
    const email = get(state, 'buyerInfo.buyerInfo.email');
    const hasVinReminder = get(state, 'navigation.setVinReminder', false);
    const hasRegReminder = get(state, 'navigation.setRegReminder', false);
    const lienholderName = get(vehicleInfo, 'lienholder.company.name');
    const defaultVehicleInfoType = get(vehicleInfo, 'vehicleInfoType');
    const isCU = dealType === 'creditUnion';
    const isDealer = dealType === 'dealer';
    const vehicleInfoType = isDealer && defaultVehicleInfoType === 'license' ? 'vin' : defaultVehicleInfoType;

    return {
        ...vehicleInfo,
        accessories,
        addressState,
        buyerName,
        contactPhone,
        currMonthlyPayment,
        dealType: get(state, 'creditor.type'),
        dialogData: {
            cellphone,
            email
        },
        existingRate,
        hasRegReminder,
        hasVinReminder,
        isCCRMode: SessionService.isCCRMode(),
        isCU,
        isDealer,
        knowRegistrationState,
        licensePlate,
        licenseState,
        lienholderName,
        newCurrMonthlyPayment,
        newExistingRate,
        newPurchaseDate,
        newOriginalTerm,
        offerId: state.config.offerId,
        payoff,
        prioritizedMakes,
        registrationState,
        state,
        style,
        VehicleInfoService,
        vehicleInfoType,
        yearRange
    };
};

const mapDispatchToProps = () => {
    return {
        onCurrMonthlyPaymentChange: value => {
            DealInfoService.set('newCurrMonthlyPayment', value);
        },
        onExistingRateChange: value => {
            DealInfoService.set('newExistingRate', value);
        },
        onOriginalTermChange: value => {
            DealInfoService.set('newOriginalTerm', value);
        },
        onMileageExactChange: value => {
            VehicleInfoService.set('mileageExact', value === 'exact');
        },
        onVehicleInfoTypeChange: value => {
            VehicleInfoService.set('vehicleInfoType', value);
        },
        onRegistrationStateChange: value => {
            if (value) {
                VehicleInfoService.set('registrationState', value);
            }
        },
        onKnowRegistrationStateChange: (addressState, proxy) => {
            const {value} = proxy.target;
            if (value) {
                VehicleInfoService.set('knowRegistrationState', value);

                if (value === 'yes' && addressState) {
                    VehicleInfoService.set('registrationState', addressState);
                } else {
                    VehicleInfoService.unset('registrationState');
                }
            }
        },
        onVehicleColorChange: value => {
            VehicleInfoService.set('color', value);
        },
        onLienholderNameChange: value => {
            VehicleInfoService.set('lienholder.company.name', value);
        },
        onMileageChange: value => {
            VehicleInfoService.set('mileage', value);
        }
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(injectSheet(styles)(IdentifyVehicle));
